import { NewsContext, FilteredNewsItem } from '@/types/news';
import { 
  getCachedNews, 
  cacheNews, 
  generateCacheKey, 
  cleanExpiredCache 
} from '@/lib/NewsContext/newsCaching';

interface NewsContextOptions {
  category?: string;
  location?: string;
  maxItems?: number;
  includeCategories?: string[];
  excludeKeywords?: string[];
}

interface FormattedNewsContext {
  summary: string;
  topHeadlines: Array<{
    title: string;
    category: string;
    source: string;
    timeAgo: string;
    conversationStarter: string;
  }>;
  categories: string[];
  lastUpdated: string;
  totalItems: number;
}

// Fetch news headlines from the backend
async function fetchNewsHeadlines(options: NewsContextOptions = {}): Promise<NewsContext> {
  // Clean expired cache periodically
  if (Math.random() < 0.1) { // 10% chance to clean cache on each call
    cleanExpiredCache();
  }

  // Check cache first
  const cacheKey = generateCacheKey(options);
  const cached = getCachedNews(cacheKey);
  if (cached) {
    return cached;
  }

  // Build query parameters
  const params = new URLSearchParams();
  if (options.category) params.append('category', options.category);
  if (options.maxItems) params.append('pageSize', options.maxItems.toString());

  // Fetch from backend
  const response = await fetch(`/.netlify/functions/getNewsHeadlines?${params}`);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch news: ${response.status}`);
  }

  const newsContext: NewsContext = await response.json();
  
  // Cache the response
  cacheNews(cacheKey, newsContext);
  
  return newsContext;
}

// Calculate how long ago an article was published
function getTimeAgo(publishedAt: string): string {
  const now = new Date();
  const published = new Date(publishedAt);
  const diffMs = now.getTime() - published.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffHours >= 24) {
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else if (diffHours >= 1) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffMinutes >= 1) {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
}

// Generate conversation starters based on news items
function generateConversationStarter(item: FilteredNewsItem): string {
  const starters: Record<string, string[]> = {
    technology: [
      `Have you heard about ${item.title.toLowerCase()}? It's pretty interesting how technology keeps evolving.`,
      `There's this cool tech news about ${item.title.toLowerCase()}. What do you think about these kinds of innovations?`,
      `I just read something fascinating - ${item.title.toLowerCase()}. Are you into tech stuff like this?`
    ],
    health: [
      `There's an interesting health story today - ${item.title.toLowerCase()}. Have you been following health trends lately?`,
      `Did you see the news about ${item.title.toLowerCase()}? It's got me thinking about health and wellness.`,
      `There's this health-related news - ${item.title.toLowerCase()}. Do you keep up with health discoveries?`
    ],
    entertainment: [
      `Have you heard about ${item.title.toLowerCase()}? I love staying up with entertainment news.`,
      `There's some fun entertainment news - ${item.title.toLowerCase()}. Are you into this kind of stuff?`,
      `Did you catch the news about ${item.title.toLowerCase()}? Entertainment news always makes for good conversation.`
    ],
    sports: [
      `There's some exciting sports news - ${item.title.toLowerCase()}. Do you follow sports much?`,
      `Have you seen the latest about ${item.title.toLowerCase()}? Sports news always gets people talking.`,
      `Did you hear about ${item.title.toLowerCase()}? I find sports stories pretty interesting.`
    ],
    lifestyle: [
      `I just read about ${item.title.toLowerCase()}. It's interesting how lifestyle trends keep changing.`,
      `There's this lifestyle story - ${item.title.toLowerCase()}. What do you think about these kinds of trends?`,
      `Have you heard about ${item.title.toLowerCase()}? I find lifestyle news pretty relatable.`
    ],
    science: [
      `There's fascinating science news today - ${item.title.toLowerCase()}. Do you enjoy keeping up with discoveries?`,
      `Did you see this science story - ${item.title.toLowerCase()}? I love how science keeps surprising us.`,
      `Have you heard about ${item.title.toLowerCase()}? Science news always makes for interesting conversation.`
    ]
  };

  const categoryStarters = starters[item.category] || [
    `Have you heard about ${item.title.toLowerCase()}? It caught my attention today.`,
    `There's interesting news about ${item.title.toLowerCase()}. What do you think?`,
    `Did you see the news about ${item.title.toLowerCase()}? It's pretty noteworthy.`
  ];

  return categoryStarters[Math.floor(Math.random() * categoryStarters.length)];
}

// Format news context for conversation starters
export async function formatNewsContext(options: NewsContextOptions = {}): Promise<FormattedNewsContext> {
  try {
    const newsContext = await fetchNewsHeadlines(options);
    
    // Filter by include/exclude criteria if provided
    let filteredHeadlines = newsContext.headlines;
    
    if (options.includeCategories && options.includeCategories.length > 0) {
      filteredHeadlines = filteredHeadlines.filter(item => 
        options.includeCategories!.includes(item.category)
      );
    }
    
    if (options.excludeKeywords && options.excludeKeywords.length > 0) {
      filteredHeadlines = filteredHeadlines.filter(item => {
        const text = `${item.title} ${item.description || ''}`.toLowerCase();
        return !options.excludeKeywords!.some(keyword => text.includes(keyword.toLowerCase()));
      });
    }

    // Limit results
    const maxItems = options.maxItems || 10;
    const topHeadlines = filteredHeadlines.slice(0, maxItems);

    // Format for conversation starters
    const formattedHeadlines = topHeadlines.map(item => ({
      title: item.title,
      category: item.category,
      source: item.source,
      timeAgo: getTimeAgo(item.publishedAt),
      conversationStarter: generateConversationStarter(item)
    }));

    // Generate summary
    const categoryCounts = topHeadlines.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topCategories = Object.entries(categoryCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category);

    const summary = topCategories.length > 0 
      ? `Today's conversation-friendly news features ${topCategories.join(', ')} topics, with ${topHeadlines.length} relevant headlines perfect for small talk.`
      : `Found ${topHeadlines.length} conversation-friendly headlines from various categories.`;

    return {
      summary,
      topHeadlines: formattedHeadlines,
      categories: newsContext.categories,
      lastUpdated: newsContext.lastUpdated,
      totalItems: newsContext.totalItems
    };

  } catch (error) {
    console.error('Error formatting news context:', error);
    
    // Return fallback context
    return {
      summary: 'Unable to fetch current news at the moment, but there are always interesting topics to discuss!',
      topHeadlines: [],
      categories: [],
      lastUpdated: new Date().toISOString(),
      totalItems: 0
    };
  }
}

// Get news context for specific categories
export async function getNewsByCategory(category: string, maxItems: number = 5): Promise<FormattedNewsContext> {
  return formatNewsContext({ 
    category, 
    maxItems,
    includeCategories: [category]
  });
}

// Get trending conversation topics
export async function getTrendingTopics(location?: string): Promise<string[]> {
  try {
    const newsContext = await formatNewsContext({ 
      location,
      maxItems: 20
    });

    // Extract trending topics from conversation topics
    const topicCounts = newsContext.topHeadlines.reduce((acc, item) => {
      // For now, use category as topic since conversationTopics aren't included in formatted output
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(topicCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([topic]) => topic);

  } catch (error) {
    console.error('Error getting trending topics:', error);
    return ['technology', 'health', 'entertainment', 'lifestyle', 'science'];
  }
}